import { performanceStore } from "../performance/store";

export type TeslaAudioState = "off" | "connecting" | "buffering" | "playing" | "reconnecting" | "blocked" | "unsupported" | "error";

interface Callbacks {
  onState: (state: TeslaAudioState) => void;
  onError: (error: Error) => void;
}

const AUDIO_SOCKET = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/tesla-android/sockets/audio`;
const AUDIO_MIME = 'audio/mp4; codecs="flac"';
const MAX_QUEUE_BYTES = 2 * 1024 * 1024;
const DROP_TO_BYTES = 800 * 1024;

export class TeslaAndroidAudioClient {
  private enabled = false;
  private socket?: WebSocket;
  private reconnectTimer?: number;
  private reconnectDelay = 500;
  private mediaSource?: MediaSource;
  private sourceBuffer?: SourceBuffer;
  private objectUrl?: string;
  private queue: Uint8Array[] = [];
  private queueBytes = 0;
  private bytesReceived = 0;
  private droppedBytes = 0;
  private reconnects = 0;
  private trimTimer?: number;
  private recovering = false;
  private state: TeslaAudioState = "off";
  private bufferingStartedAt?: number;
  private bufferingCount = 0;
  private bufferingTotalMs = 0;
  private bufferingMaxMs = 0;

  constructor(
    private readonly audio: HTMLAudioElement,
    private readonly callbacks: Callbacks,
  ) {
    audio.preload = "none";
    audio.muted = false;
    audio.volume = 1;
    audio.addEventListener("playing", this.onPlaying);
    audio.addEventListener("waiting", this.onBuffering);
    audio.addEventListener("stalled", this.onBuffering);
    audio.addEventListener("error", this.onMediaError);
    this.publish("off");
  }

  setEnabled(enabled: boolean) {
    if (enabled === this.enabled) {
      if (enabled) void this.tryPlay(true);
      return;
    }
    this.enabled = enabled;
    if (!enabled) {
      this.stop();
      return;
    }
    if (!("MediaSource" in window) || !MediaSource.isTypeSupported(AUDIO_MIME)) {
      this.publish("unsupported");
      this.callbacks.onError(new Error("This browser cannot decode TeslaAndroid's lossless FLAC audio stream"));
      return;
    }

    this.audio.muted = false;
    this.setupPipeline();
    this.openSocket();
    // This call occurs directly inside the user's sound-button event. It
    // unlocks playback before the first media segment arrives.
    void this.tryPlay(true);
  }

  disconnect() {
    this.enabled = false;
    this.stop();
    this.audio.removeEventListener("playing", this.onPlaying);
    this.audio.removeEventListener("waiting", this.onBuffering);
    this.audio.removeEventListener("stalled", this.onBuffering);
    this.audio.removeEventListener("error", this.onMediaError);
  }

  private openSocket() {
    if (!this.enabled || this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;
    this.publish(this.reconnects ? "reconnecting" : "connecting");
    const socket = new WebSocket(AUDIO_SOCKET);
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    socket.onopen = () => {
      this.reconnectDelay = 500;
      this.publish("buffering");
    };
    socket.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        this.enqueue(new Uint8Array(event.data));
      } else if (event.data instanceof Blob) {
        void event.data.arrayBuffer().then((value) => {
          if (this.enabled) this.enqueue(new Uint8Array(value));
        });
      }
    };
    socket.onerror = () => {
      if (this.enabled) this.publish("reconnecting");
    };
    socket.onclose = () => {
      if (this.socket === socket) this.socket = undefined;
      if (this.enabled) this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (!this.enabled || this.reconnectTimer !== undefined) return;
    this.reconnects += 1;
    this.publish("reconnecting");
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.openSocket();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(10_000, this.reconnectDelay * 2);
  }

  private setupPipeline() {
    if (!this.enabled || this.mediaSource) return;
    const mediaSource = new MediaSource();
    this.mediaSource = mediaSource;
    this.objectUrl = URL.createObjectURL(mediaSource);
    this.audio.src = this.objectUrl;
    this.audio.load();
    mediaSource.addEventListener("sourceopen", this.onSourceOpen, { once: true });
    this.trimTimer = window.setInterval(() => {
      this.trimOldAudio();
      this.reduceLatency();
      this.publishBufferMetrics();
    }, 1000);
  }

  private readonly onSourceOpen = () => {
    if (!this.enabled || !this.mediaSource || this.mediaSource.readyState !== "open") return;
    try {
      const sourceBuffer = this.mediaSource.addSourceBuffer(AUDIO_MIME);
      sourceBuffer.mode = "sequence";
      sourceBuffer.addEventListener("updateend", this.onUpdateEnd);
      sourceBuffer.addEventListener("error", this.onSourceBufferError);
      this.sourceBuffer = sourceBuffer;
      this.processQueue();
      void this.tryPlay(false);
    } catch (error) {
      this.fail(error);
    }
  };

  private enqueue(segment: Uint8Array) {
    if (!segment.byteLength) return;
    this.bytesReceived += segment.byteLength;
    while (this.queueBytes + segment.byteLength > MAX_QUEUE_BYTES && this.queue.length) {
      const dropped = this.queue.shift();
      if (dropped) {
        this.queueBytes -= dropped.byteLength;
        this.droppedBytes += dropped.byteLength;
      }
    }
    if (segment.byteLength > MAX_QUEUE_BYTES) {
      this.droppedBytes += segment.byteLength;
      this.publishBufferMetrics();
      return;
    }
    this.queue.push(segment);
    this.queueBytes += segment.byteLength;
    if (this.queueBytes > MAX_QUEUE_BYTES) {
      while (this.queueBytes > DROP_TO_BYTES && this.queue.length > 1) {
        const dropped = this.queue.shift();
        if (dropped) {
          this.queueBytes -= dropped.byteLength;
          this.droppedBytes += dropped.byteLength;
        }
      }
    }
    this.processQueue();
    this.publishBufferMetrics();
  }

  private processQueue() {
    const sourceBuffer = this.sourceBuffer;
    if (!this.enabled || !sourceBuffer || sourceBuffer.updating || !this.queue.length) return;
    const segment = this.queue.shift();
    if (!segment) return;
    this.queueBytes -= segment.byteLength;
    try {
      sourceBuffer.appendBuffer(
        segment.buffer.slice(segment.byteOffset, segment.byteOffset + segment.byteLength) as ArrayBuffer,
      );
    } catch (error) {
      this.queue.unshift(segment);
      this.queueBytes += segment.byteLength;
      this.recoverPipeline(error);
    }
  }

  private readonly onUpdateEnd = () => {
    this.reduceLatency();
    this.trimOldAudio();
    this.processQueue();
    this.publishBufferMetrics();
    void this.tryPlay(false);
  };

  private reduceLatency() {
    const buffered = this.audio.buffered;
    if (!buffered.length) return;
    const liveEdge = buffered.end(buffered.length - 1);
    const lag = liveEdge - this.audio.currentTime;
    if (lag > 1) this.audio.currentTime = Math.max(0, liveEdge - 0.1);
  }

  private trimOldAudio() {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || sourceBuffer.updating || !sourceBuffer.buffered.length || this.audio.currentTime <= 4) return;
    const start = sourceBuffer.buffered.start(0);
    const end = Math.max(start, this.audio.currentTime - 0.2);
    if (end > start + 0.01) {
      try { sourceBuffer.remove(start, end); } catch { /* A later timer will retry. */ }
    }
  }

  private bufferedSeconds() {
    const buffered = this.audio.buffered;
    if (!buffered.length) return 0;
    return Math.max(0, buffered.end(buffered.length - 1) - this.audio.currentTime);
  }

  private publishBufferMetrics() {
    const activeBufferingMs = this.bufferingStartedAt === undefined
      ? 0
      : performance.now() - this.bufferingStartedAt;
    performanceStore.update({
      audioState: this.state,
      audioCodec: "FLAC in fragmented MP4",
      audioBytesReceived: this.bytesReceived,
      audioBufferedSeconds: Math.round(this.bufferedSeconds() * 100) / 100,
      audioReconnectCount: this.reconnects,
      audioDroppedBytes: this.droppedBytes,
      audioBufferingCount: this.bufferingCount,
      audioBufferingTotalMs: Math.round((this.bufferingTotalMs + activeBufferingMs) * 10) / 10,
      audioBufferingMaxMs: Math.round(Math.max(this.bufferingMaxMs, activeBufferingMs) * 10) / 10,
    });
  }

  private readonly onPlaying = () => {
    if (!this.enabled) return;
    if (this.bufferingStartedAt !== undefined) {
      const duration = performance.now() - this.bufferingStartedAt;
      this.bufferingTotalMs += duration;
      this.bufferingMaxMs = Math.max(this.bufferingMaxMs, duration);
      this.bufferingStartedAt = undefined;
    }
    this.publish("playing");
    this.publishBufferMetrics();
  };

  private readonly onBuffering = () => {
    if (!this.enabled || this.state === "connecting" || this.state === "reconnecting") return;
    if (this.state === "playing" && this.bufferingStartedAt === undefined) {
      this.bufferingStartedAt = performance.now();
      this.bufferingCount += 1;
    }
    this.publish("buffering");
    this.publishBufferMetrics();
  };

  private readonly onMediaError = () => {
    if (this.enabled) this.recoverPipeline(this.audio.error?.message || "TeslaAndroid audio element failed");
  };

  private readonly onSourceBufferError = () => {
    if (this.enabled) this.recoverPipeline("TeslaAndroid audio buffer failed");
  };

  private recoverPipeline(error: unknown) {
    if (!this.enabled || this.recovering) return;
    this.recovering = true;
    this.publish("buffering");
    window.setTimeout(() => {
      if (this.socket) {
        this.socket.onclose = null;
        this.socket.close();
      }
      this.socket = undefined;
      this.queue = [];
      this.queueBytes = 0;
      this.destroyPipeline();
      this.recovering = false;
      if (this.enabled) {
        this.setupPipeline();
        this.openSocket();
      }
    }, 500);
    if (error instanceof DOMException && error.name === "QuotaExceededError") return;
    console.warn("TeslaAndroid audio pipeline recovery", error);
  }

  private async tryPlay(reportBlocked: boolean) {
    if (!this.enabled) return;
    try {
      await this.audio.play();
    } catch (error) {
      const mediaError = error instanceof DOMException ? error : undefined;
      if (reportBlocked && mediaError?.name === "NotAllowedError") {
        this.publish("blocked");
        this.callbacks.onError(new Error("Browser blocked sound playback; click Sound once more to enable it"));
      }
      // AbortError and NotSupportedError are expected before the first source
      // buffer segment. onUpdateEnd retries playback when media is available.
    }
  }

  private fail(error: unknown) {
    this.publish("error");
    this.callbacks.onError(error instanceof Error ? error : new Error(String(error)));
  }

  private stop() {
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
    }
    this.socket = undefined;
    this.reconnectDelay = 500;
    this.queue = [];
    this.queueBytes = 0;
    this.bufferingStartedAt = undefined;
    this.destroyPipeline();
    this.publish("off");
  }

  private destroyPipeline() {
    if (this.trimTimer !== undefined) window.clearInterval(this.trimTimer);
    this.trimTimer = undefined;
    if (this.sourceBuffer) {
      this.sourceBuffer.removeEventListener("updateend", this.onUpdateEnd);
      this.sourceBuffer.removeEventListener("error", this.onSourceBufferError);
      if (this.mediaSource?.readyState === "open") {
        try { this.sourceBuffer.abort(); } catch { /* Already detached. */ }
        try { this.mediaSource.removeSourceBuffer(this.sourceBuffer); } catch { /* Already detached. */ }
      }
    }
    this.sourceBuffer = undefined;
    this.mediaSource = undefined;
    this.audio.pause();
    this.audio.removeAttribute("src");
    this.audio.load();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = undefined;
  }

  private publish(state: TeslaAudioState) {
    this.state = state;
    performanceStore.update({
      audioState: state,
      audioCodec: "FLAC in fragmented MP4",
      audioReconnectCount: this.reconnects,
    });
    this.callbacks.onState(state);
  }
}
